<template>
  <v-container fluid class="pl-4 pr-4 pb-4 pt-1">
    <no-projects-alert v-if="noProjects"></no-projects-alert>
    <!-- Header with project chips and controls -->
    <v-row align="center" class="mb-0 px-0" style="min-height: 48px;">
      <v-col cols="auto" class="d-flex align-center ga-2 flex-wrap">
        <v-chip
          v-for="project in projects"
          :key="project.id"
          :color="selectedProjectId === project.id ? 'primary' : 'default'"
          :variant="selectedProjectId === project.id ? 'elevated' : 'outlined'"
          @click="selectProject(project.id)"
          class="cursor-pointer"
        >
          {{ project.name }}
        </v-chip>
      </v-col>
      <v-spacer />
      <v-col cols="auto">
        <HelpHint section="reports" anchor="reports" text="Шаблоны, отчёты PDF и CSV, рассылка по почте" />
        <v-btn color="primary" prepend-icon="mdi-plus" @click="showCreateDialog = true">
          Создать шаблон
        </v-btn>
      </v-col>
    </v-row>

    <v-row v-if="selectedProjectId">
      <v-col cols="12">
        <!-- Tabs: Templates / Generated reports -->
        <v-card>
          <v-tabs v-model="activeTab" color="primary" density="compact">
            <v-tab value="templates">
              <v-icon start size="small">mdi-file-document-edit</v-icon>
              Шаблоны ({{ templates.length }})
            </v-tab>
            <v-tab value="reports">
              <v-icon start size="small">mdi-file-chart</v-icon>
              Отчёты ({{ reports.length }})
            </v-tab>
          </v-tabs>

          <v-card-text class="pa-0">
            <!-- Templates tab -->
            <v-window v-model="activeTab">
              <v-window-item value="templates">
                <v-table v-if="templates.length > 0" density="compact" hover>
                  <thead>
                    <tr>
                      <th class="font-weight-bold">Название</th>
                      <th class="font-weight-bold">Блоки</th>
                      <th class="font-weight-bold">Период</th>
                      <th class="font-weight-bold">Рассылка</th>
                      <th class="font-weight-bold">Изменён</th>
                      <th class="font-weight-bold" style="width: 140px">Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="t in templates" :key="t.id">
                      <td>
                        <div class="font-weight-medium">{{ t.name }}</div>
                        <div v-if="t.description" class="text-caption text-medium-emphasis">{{ t.description }}</div>
                      </td>
                      <td>
                        <v-chip
                          v-for="block in getEnabledBlocks(t.blocks)"
                          :key="block.type"
                          size="x-small"
                          class="mr-1 mb-1"
                          variant="tonal"
                        >
                          {{ blockTypeLabels[block.type] || block.type }}
                        </v-chip>
                      </td>
                      <td>{{ t.default_period_days }} дн.</td>
                      <td>
                        <v-icon v-if="t._schedule && t._schedule.is_active" color="success" size="small">mdi-check-circle</v-icon>
                        <v-icon v-else color="grey" size="small">mdi-minus-circle</v-icon>
                      </td>
                      <td>{{ formatDate(t.updated_at) }}</td>
                      <td>
                        <v-tooltip text="Сгенерировать отчёт" location="top">
                          <template v-slot:activator="{ props }">
                            <v-btn
                              v-bind="props"
                              icon="mdi-play"
                              size="x-small"
                              variant="text"
                              color="success"
                              @click="generateFromTemplate(t)"
                            />
                          </template>
                        </v-tooltip>
                        <v-tooltip text="Редактировать" location="top">
                          <template v-slot:activator="{ props }">
                            <v-btn
                              v-bind="props"
                              icon="mdi-pencil"
                              size="x-small"
                              variant="text"
                              color="primary"
                              @click="editTemplate(t)"
                            />
                          </template>
                        </v-tooltip>
                        <v-tooltip text="Удалить" location="top">
                          <template v-slot:activator="{ props }">
                            <v-btn
                              v-bind="props"
                              icon="mdi-delete"
                              size="x-small"
                              variant="text"
                              color="error"
                              @click="confirmDeleteTemplate(t)"
                            />
                          </template>
                        </v-tooltip>
                      </td>
                    </tr>
                  </tbody>
                </v-table>
                <div v-else class="pa-8 text-center text-medium-emphasis">
                  <v-icon size="64" color="grey-lighten-1">mdi-file-document-plus-outline</v-icon>
                  <div class="mt-2">Нет шаблонов. Создайте первый шаблон отчёта.</div>
                </div>
              </v-window-item>

              <!-- Reports tab -->
              <v-window-item value="reports">
                <v-table v-if="reports.length > 0" density="compact" hover>
                  <thead>
                    <tr>
                      <th class="font-weight-bold">Название</th>
                      <th class="font-weight-bold">Период</th>
                      <th class="font-weight-bold">Статус</th>
                      <th class="font-weight-bold">Создан</th>
                      <th class="font-weight-bold" style="width: 140px">Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="r in reports" :key="r.id">
                      <td>{{ r.title }}</td>
                      <td>{{ formatDate(r.period_from) }} — {{ formatDate(r.period_to) }}</td>
                      <td>
                        <v-chip :color="statusColor(r.status)" size="x-small" variant="tonal">
                          {{ statusLabel(r.status) }}
                        </v-chip>
                      </td>
                      <td>{{ formatDateTime(r.created_at) }}</td>
                      <td>
                        <v-tooltip v-if="r.status === 'ready'" text="Скачать PDF" location="top">
                          <template v-slot:activator="{ props }">
                            <v-btn
                              v-bind="props"
                              icon="mdi-file-pdf-box"
                              size="x-small"
                              variant="text"
                              color="error"
                              @click="downloadReport(r.id, 'pdf')"
                            />
                          </template>
                        </v-tooltip>
                        <v-tooltip v-if="r.status === 'ready'" text="Скачать CSV" location="top">
                          <template v-slot:activator="{ props }">
                            <v-btn
                              v-bind="props"
                              icon="mdi-file-delimited"
                              size="x-small"
                              variant="text"
                              color="success"
                              @click="downloadReport(r.id, 'csv')"
                            />
                          </template>
                        </v-tooltip>
                        <v-tooltip text="Удалить" location="top">
                          <template v-slot:activator="{ props }">
                            <v-btn
                              v-bind="props"
                              icon="mdi-delete"
                              size="x-small"
                              variant="text"
                              color="error"
                              @click="confirmDeleteReport(r)"
                            />
                          </template>
                        </v-tooltip>
                      </td>
                    </tr>
                  </tbody>
                </v-table>
                <div v-else class="pa-8 text-center text-medium-emphasis">
                  <v-icon size="64" color="grey-lighten-1">mdi-file-chart-outline</v-icon>
                  <div class="mt-2">Нет сгенерированных отчётов.</div>
                </div>
              </v-window-item>
            </v-window>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-else>
      <v-col cols="12">
        <div class="pa-8 text-center text-medium-emphasis">
          Выберите проект для работы с отчётами
        </div>
      </v-col>
    </v-row>

    <!-- Create/Edit template dialog -->
    <v-dialog v-model="showCreateDialog" max-width="700" @keydown.esc="closeDialog">
      <v-card>
        <v-card-title>{{ editingTemplate ? 'Редактировать шаблон' : 'Новый шаблон отчёта' }}</v-card-title>
        <v-card-text>
          <v-alert
            v-if="formError"
            type="error"
            variant="tonal"
            closable
            class="mb-3"
            @click:close="formError = ''"
          >
            {{ formError }}
          </v-alert>
          <v-text-field
            v-model="templateForm.name"
            label="Название шаблона"
            variant="outlined"
            density="compact"
            class="mb-3"
            :rules="[v => !!v || 'Обязательное поле']"
          />
          <v-textarea
            v-model="templateForm.description"
            label="Описание"
            variant="outlined"
            density="compact"
            rows="2"
            class="mb-3"
          />
          <v-text-field
            v-model.number="templateForm.default_period_days"
            label="Период по умолчанию (дней)"
            type="number"
            variant="outlined"
            density="compact"
            class="mb-4"
            min="1"
            max="100"
            :rules="[v => (v >= 1 && v <= 100) || 'От 1 до 100 дней']"
          />

          <div class="text-subtitle-2 mb-2">Блоки отчёта</div>
          <div v-for="(block, idx) in templateForm.blocks" :key="idx" class="mb-2">
            <v-card variant="outlined" class="pa-3">
              <div class="d-flex align-center">
                <v-checkbox
                  v-model="block.enabled"
                  :label="blockTypeLabels[block.type]"
                  density="compact"
                  hide-details
                  class="flex-grow-1"
                />
                <v-icon size="small" color="grey">{{ blockTypeIcons[block.type] }}</v-icon>
              </div>
              <!-- Block-specific params -->
              <div v-if="block.type === 'top_entities' && block.enabled" class="mt-2 ml-8">
                <v-text-field
                  v-model.number="block.params.limit"
                  label="Количество сущностей"
                  type="number"
                  variant="outlined"
                  density="compact"
                  :rules="[v => !!v || 'Обязательное поле']"
                  style="max-width: 200px"
                />
              </div>
              <div v-if="block.type === 'findings_table' && block.enabled" class="mt-2 ml-8">
                <v-text-field
                  v-model.number="block.params.limit"
                  label="Количество сообщений"
                  type="number"
                  variant="outlined"
                  density="compact"
                  :rules="[v => !!v || 'Обязательное поле']"
                  style="max-width: 200px"
                />
              </div>
            </v-card>
          </div>

          <!-- Schedule section -->
          <div class="text-subtitle-2 mb-2 mt-4">Рассылка</div>
          <v-card variant="outlined" class="pa-3">
            <v-checkbox
              v-model="scheduleForm.is_active"
              label="Включить автоматическую рассылку"
              density="compact"
              hide-details
            />
            <div v-if="scheduleForm.is_active" class="mt-3">
              <v-select
                v-model="scheduleForm.frequency_hours"
                :items="frequencyOptions"
                item-title="title"
                item-value="value"
                label="Частота"
                variant="outlined"
                density="compact"
                hide-details
                class="mb-3"
              />
              <div class="text-body-2 text-medium-emphasis mb-3">
                Отчёт будет отправлен на <strong>{{ userEmail }}</strong>
              </div>
              <div class="d-flex ga-3">
                <v-checkbox v-model="scheduleForm.send_pdf" label="PDF" density="compact" hide-details />
                <v-checkbox v-model="scheduleForm.send_csv" label="CSV" density="compact" hide-details />
              </div>
              <v-alert v-if="scheduleForm.send_csv" type="info" variant="tonal" density="compact" class="mt-2">
                В формате CSV выводится только список найденных сообщений
              </v-alert>
            </div>
          </v-card>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="closeDialog">Отмена</v-btn>
          <v-btn color="primary" variant="elevated" @click="saveTemplate" :loading="saving">
            {{ editingTemplate ? 'Сохранить' : 'Создать' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Generate report dialog -->
    <v-dialog v-model="showGenerateDialog" max-width="450" @keydown.esc="showGenerateDialog = false">
      <v-card>
        <v-card-title>Генерация отчёта</v-card-title>
        <v-card-text>
          <v-alert v-if="generateError" type="error" variant="tonal" density="compact" class="mb-3" closable @click:close="generateError = ''">
            {{ generateError }}
          </v-alert>
          <div class="text-body-2 mb-3">Шаблон: <strong>{{ generateForm.templateName }}</strong></div>
          <v-text-field
            v-model="generateForm.period_from"
            label="Начало периода"
            type="date"
            variant="outlined"
            density="compact"
            class="mb-3"
            :max="generateForm.period_to || todayDate"
            :min="minGenerateDate"
          />
          <v-text-field
            v-model="generateForm.period_to"
            label="Конец периода"
            type="date"
            variant="outlined"
            density="compact"
            :max="todayDate"
            :min="generateForm.period_from"
          />
          <div class="text-caption text-grey mt-1">Максимальный период — 100 дней</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="showGenerateDialog = false">Отмена</v-btn>
          <v-btn color="primary" variant="elevated" @click="doGenerate">
            Сгенерировать
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirm delete dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title>Подтверждение удаления</v-card-title>
        <v-card-text>{{ deleteMessage }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="showDeleteDialog = false">Отмена</v-btn>
          <v-btn color="error" variant="elevated" @click="doDelete">Удалить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="5000" location="top">
      {{ snackbarText }}
      <template v-slot:actions>
        <v-btn icon="mdi-close" variant="text" size="small" @click="snackbar = false" />
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import NoProjectsAlert from "@/components/projects/NoProjectsAlert.vue";
import apiClient from '@/apiClient';
import store from '@/store';

const COOKIE_PREFIX = 'reports_';
const COOKIE_MAX_AGE = 31536000; // 1 year

export default {
  name: 'Reports',
  components: { NoProjectsAlert },
  data() {
    return {
      noProjects: false,
      projects: [],
      selectedProjectId: null,
      activeTab: 'templates',
      templates: [],
      reports: [],
      loading: false,
      saving: false,
      // Template form
      showCreateDialog: false,
      editingTemplate: null,
      templateForm: this.getDefaultTemplateForm(),
      scheduleForm: this.getDefaultScheduleForm(),

      // Generate form
      showGenerateDialog: false,
      generateForm: {
        templateId: null,
        templateName: '',
        period_from: '',
        period_to: '',
      },

      // Delete
      showDeleteDialog: false,
      deleteMessage: '',
      deleteAction: null,

      // Form error (shown inside dialog)
      formError: '',
      generateError: '',

      // Snackbar
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',

      // Labels
      blockTypeLabels: {
        llm_summary: 'Краткая сводка',
        top_entities: 'Топ сущностей',
        findings_table: 'Таблица сообщений',
        statistics: 'Статистика',
        sentiment_chart: 'Тональность',
      },
      blockTypeIcons: {
        llm_summary: 'mdi-robot',
        top_entities: 'mdi-account-group',
        findings_table: 'mdi-table',
        statistics: 'mdi-chart-bar',
        sentiment_chart: 'mdi-emoticon-outline',
      },
      frequencyOptions: [
        { title: 'Ежедневно', value: 24 },
        { title: 'Раз в 2 дня', value: 48 },
        { title: 'Еженедельно', value: 168 },
      ],
    };
  },
  computed: {
    userEmail() {
      return store.state.userInfo?.email || '';
    },
    todayDate() {
      return new Date().toISOString().slice(0, 10);
    },
    minGenerateDate() {
      const d = new Date();
      d.setDate(d.getDate() - 100);
      return d.toISOString().slice(0, 10);
    },
  },
  methods: {
    getDefaultTemplateForm() {
      return {
        name: '',
        description: '',
        default_period_days: 7,
        blocks: [
          { type: 'llm_summary', enabled: false, params: { max_tokens: 1000 } },
          { type: 'top_entities', enabled: false, params: { limit: 10, entity_types: ['PER', 'ORG'] } },
          { type: 'findings_table', enabled: false, params: {} },
          { type: 'statistics', enabled: false, params: {} },
          { type: 'sentiment_chart', enabled: false, params: {} },
        ],
      };
    },
    getDefaultScheduleForm() {
      return {
        is_active: false,
        frequency_hours: 24,
        send_pdf: true,
        send_csv: false,
      };
    },
    setCookie(key, value) {
      document.cookie = `${COOKIE_PREFIX}${key}=${encodeURIComponent(value)};path=/;max-age=${COOKIE_MAX_AGE}`;
    },
    getCookie(key) {
      const match = document.cookie.match(new RegExp(`${COOKIE_PREFIX}${key}=([^;]+)`));
      return match ? decodeURIComponent(match[1]) : null;
    },
    async loadProjects() {
      try {
        const resp = await apiClient.get('/available-projects');
        this.projects = resp.data || [];
        this.noProjects = !this.projects.length;
        if (this.projects.length > 0 && !this.selectedProjectId) {
          const savedId = this.getCookie('project');
          const restoredId = savedId ? parseInt(savedId, 10) : null;
          const targetId = this.projects.find(p => p.id === restoredId) ? restoredId : this.projects[0].id;
          this.selectProject(targetId);
        }
        // Restore saved tab
        const savedTab = this.getCookie('tab');
        if (savedTab && ['templates', 'reports'].includes(savedTab)) {
          this.activeTab = savedTab;
        }
      } catch (e) {
        console.error('Failed to load projects:', e);
      }
    },
    async selectProject(projectId) {
      this.selectedProjectId = projectId;
      this.setCookie('project', projectId);
      await Promise.all([this.loadTemplates(), this.loadReports()]);
    },
    async loadTemplates() {
      if (!this.selectedProjectId) return;
      try {
        const resp = await apiClient.get(`/projects/${this.selectedProjectId}/reports/templates`);
        const items = resp.data.items || [];
        // Load schedule for each template
        for (const t of items) {
          try {
            const schedResp = await apiClient.get(
              `/projects/${this.selectedProjectId}/reports/templates/${t.id}/schedule`
            );
            t._schedule = schedResp.data;
          } catch {
            t._schedule = null;
          }
        }
        this.templates = items;
      } catch (e) {
        console.error('Failed to load templates:', e);
      }
    },
    async loadReports() {
      if (!this.selectedProjectId) return;
      try {
        const resp = await apiClient.get(`/projects/${this.selectedProjectId}/reports`);
        this.reports = resp.data.items || [];
      } catch (e) {
        console.error('Failed to load reports:', e);
      }
    },
    getEnabledBlocks(blocks) {
      return (blocks || []).filter(b => b.enabled);
    },
    editTemplate(template) {
      this.editingTemplate = template;
      this.templateForm = {
        name: template.name,
        description: template.description || '',
        default_period_days: template.default_period_days,
        blocks: JSON.parse(JSON.stringify(template.blocks)),
      };
      // Load schedule
      if (template._schedule) {
        const s = template._schedule;
        this.scheduleForm = {
          is_active: s.is_active,
          frequency_hours: s.frequency_hours,
          send_pdf: s.send_pdf,
          send_csv: s.send_csv,
        };
      } else {
        this.scheduleForm = this.getDefaultScheduleForm();
      }
      this.showCreateDialog = true;
    },
    async saveTemplate() {
      this.formError = '';
      if (!this.templateForm.name) {
        this.formError = 'Введите название шаблона';
        return;
      }
      // At least one block must be enabled
      if (!this.templateForm.blocks.some(b => b.enabled)) {
        this.formError = 'Включите хотя бы один блок';
        return;
      }
      // Validate required block params
      for (const block of this.templateForm.blocks) {
        if (!block.enabled) continue;
        if ((block.type === 'top_entities' || block.type === 'findings_table') && !block.params.limit) {
          this.formError = 'Заполните количество для включённых блоков';
          return;
        }
      }
      // Validate schedule format selection
      if (this.scheduleForm.is_active && !this.scheduleForm.send_pdf && !this.scheduleForm.send_csv) {
        this.formError = 'Выберите формат рассылки: PDF или CSV';
        return;
      }
      this.saving = true;
      try {
        const payload = {
          name: this.templateForm.name,
          description: this.templateForm.description,
          default_period_days: this.templateForm.default_period_days,
          blocks: this.templateForm.blocks,
        };

        let templateId;
        if (this.editingTemplate) {
          await apiClient.patch(
            `/projects/${this.selectedProjectId}/reports/templates/${this.editingTemplate.id}`,
            payload
          );
          templateId = this.editingTemplate.id;
          this.showSnackbar('Шаблон обновлён');
        } else {
          const resp = await apiClient.post(
            `/projects/${this.selectedProjectId}/reports/templates`,
            payload
          );
          templateId = resp.data.id;
          this.showSnackbar('Шаблон создан');
        }

        // Save schedule
        if (this.scheduleForm.is_active) {
          await apiClient.put(
            `/projects/${this.selectedProjectId}/reports/templates/${templateId}/schedule`,
            {
              is_active: true,
              frequency_hours: this.scheduleForm.frequency_hours,
              recipients: { emails: [this.userEmail], telegram_chat_ids: [] },
              send_pdf: this.scheduleForm.send_pdf,
              send_csv: this.scheduleForm.send_csv,
            }
          );
        } else if (this.editingTemplate) {
          // Disable schedule if it was active
          try {
            await apiClient.delete(
              `/projects/${this.selectedProjectId}/reports/templates/${templateId}/schedule`
            );
          } catch { /* no schedule existed */ }
        }

        this.closeDialog();
        await this.loadTemplates();
      } catch (e) {
        console.error('Failed to save template:', e);
        this.showSnackbar('Ошибка сохранения', 'error');
      } finally {
        this.saving = false;
      }
    },
    closeDialog() {
      this.showCreateDialog = false;
      this.editingTemplate = null;
      this.formError = '';
      this.templateForm = this.getDefaultTemplateForm();
      this.scheduleForm = this.getDefaultScheduleForm();
    },
    generateFromTemplate(template) {
      const now = new Date();
      const from = new Date(now);
      from.setDate(from.getDate() - template.default_period_days);
      this.generateForm = {
        templateId: template.id,
        templateName: template.name,
        period_from: from.toISOString().slice(0, 10),
        period_to: now.toISOString().slice(0, 10),
      };
      this.generateError = '';
      this.showGenerateDialog = true;
    },
    doGenerate() {
      // Validate period
      const from = new Date(this.generateForm.period_from);
      const to = new Date(this.generateForm.period_to);
      if (!this.generateForm.period_from || !this.generateForm.period_to) {
        this.generateError = 'Укажите начало и конец периода';
        return;
      }
      if (from >= to) {
        this.generateError = 'Начало периода должно быть раньше конца';
        return;
      }
      const diffDays = Math.ceil((to - from) / (1000 * 60 * 60 * 24));
      if (diffDays > 100) {
        this.generateError = 'Период не может превышать 100 дней';
        return;
      }
      this.generateError = '';
      this.showGenerateDialog = false;
      this.showSnackbar('Генерация отчёта запущена...');
      apiClient.post(`/projects/${this.selectedProjectId}/reports/generate`, {
        template_id: this.generateForm.templateId,
        period_from: new Date(this.generateForm.period_from).toISOString(),
        period_to: new Date(this.generateForm.period_to).toISOString(),
        format: 'json',
      }).then((resp) => {
        this.loadReports();
        // Poll until report is ready
        if (resp.data && resp.data.id) {
          this.pollReportStatus(resp.data.id);
        }
      }).catch((e) => {
        console.error('Failed to generate report:', e);
        this.showSnackbar('Ошибка генерации отчёта', 'error');
      });
    },
    pollReportStatus(reportId) {
      const maxAttempts = 60; // 5 minutes max
      let attempt = 0;
      const poll = setInterval(async () => {
        attempt++;
        try {
          const resp = await apiClient.get(`/projects/${this.selectedProjectId}/reports/${reportId}`);
          const status = resp.data.status;
          if (status === 'ready') {
            clearInterval(poll);
            this.loadReports();
            this.showSnackbar('Отчёт сгенерирован');
          } else if (status === 'failed' || attempt >= maxAttempts) {
            clearInterval(poll);
            this.loadReports();
            if (status === 'failed') {
              this.showSnackbar('Ошибка генерации отчёта', 'error');
            }
          }
        } catch {
          clearInterval(poll);
        }
      }, 5000); // every 5 seconds
    },
    downloadReport(reportId, fmt) {
      const url = `${process.env.VUE_APP_API_BASE_URL}/projects/${this.selectedProjectId}/reports/${reportId}/download/${fmt}`;
      window.open(url, '_blank');
    },
    confirmDeleteTemplate(template) {
      this.deleteMessage = `Удалить шаблон "${template.name}"?`;
      this.deleteAction = async () => {
        await apiClient.delete(`/projects/${this.selectedProjectId}/reports/templates/${template.id}`);
        await this.loadTemplates();
        this.showSnackbar('Шаблон удалён');
      };
      this.showDeleteDialog = true;
    },
    confirmDeleteReport(report) {
      this.deleteMessage = `Удалить отчёт "${report.title}"?`;
      this.deleteAction = async () => {
        await apiClient.delete(`/projects/${this.selectedProjectId}/reports/${report.id}`);
        await this.loadReports();
        this.showSnackbar('Отчёт удалён');
      };
      this.showDeleteDialog = true;
    },
    async doDelete() {
      try {
        if (this.deleteAction) await this.deleteAction();
      } catch (e) {
        console.error('Delete failed:', e);
        this.showSnackbar('Ошибка удаления', 'error');
      } finally {
        this.showDeleteDialog = false;
        this.deleteAction = null;
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      try {
        return new Date(dateStr).toLocaleDateString('ru-RU', {
          day: '2-digit', month: '2-digit', year: 'numeric'
        });
      } catch {
        return dateStr;
      }
    },
    formatDateTime(dateStr) {
      if (!dateStr) return '';
      try {
        const d = new Date(dateStr);
        const date = d.toLocaleDateString('ru-RU', {
          day: '2-digit', month: '2-digit', year: 'numeric'
        });
        const time = d.toLocaleTimeString('ru-RU', {
          hour: '2-digit', minute: '2-digit'
        });
        return `${date} ${time}`;
      } catch {
        return dateStr;
      }
    },
    statusColor(status) {
      return { ready: 'success', pending: 'grey', generating: 'info', failed: 'error' }[status] || 'grey';
    },
    statusLabel(status) {
      return { ready: 'Готов', pending: 'Ожидание', generating: 'Генерация...', failed: 'Ошибка' }[status] || status;
    },
    showSnackbar(text, color = 'success') {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },
  },
  watch: {
    activeTab(val) {
      this.setCookie('tab', val);
    },
  },
  async mounted() {
    await this.loadProjects();
  },
};
</script>
